import { FC, memo, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { FeeConfigGroupViewModel, FeeConfigurationViewModel, FeeHudLineViewModel } from '../../models/fees';
import styles from './FeeConfigGroupTree.module.css';

const INDENT_PER_LEVEL_REM = 1.5;

export interface FeeConfigGroupTreeProps {
  groups: FeeConfigGroupViewModel[];
  configurations: FeeConfigurationViewModel[];
  hudLineMap: Map<number, FeeHudLineViewModel>;
  isDraft: boolean;
  search: string;
  /** True when any filter (search or HUD line) is applied; empty groups are then hidden. */
  filterActive?: boolean;
  selectedConfigIds: Set<number>;
  collapsedGroupIds: Set<number>;
  ungroupedCollapsed: boolean;
  onToggleConfigSelected: (args: { configId: number }) => void;
  onToggleCollapse: (args: { groupId: number }) => void;
  onToggleUngrouped: () => void;
  onEditConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onCopyConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onMoveConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onDeleteConfig: (args: { config: FeeConfigurationViewModel; target: HTMLElement }) => void;
  onAddSubGroup: (args: { parent: FeeConfigGroupViewModel }) => void;
  onEditGroupLogic: (args: { group: FeeConfigGroupViewModel }) => void;
  onRenameGroup: (args: { group: FeeConfigGroupViewModel }) => void;
  onDeleteGroup: (args: { group: FeeConfigGroupViewModel; target: HTMLElement }) => void;
}

const configMatchesSearch = (
  config: FeeConfigurationViewModel,
  hudLineMap: Map<number, FeeHudLineViewModel>,
  searchLower: string
): boolean => {
  if (!searchLower) return true;
  const hudLineValue = config.feeHudLineId != null ? hudLineMap.get(config.feeHudLineId)?.hudLine ?? '' : '';
  return (
    (config.description?.toLowerCase().includes(searchLower) ?? false) ||
    hudLineValue.toString().toLowerCase().includes(searchLower)
  );
};

// Descriptors are plain data built during the tree walk; each maps to one memoized row so a
// collapse/expand or selection change re-renders only the rows whose own primitive props changed.
interface GroupRowData {
  kind: 'group';
  key: string;
  group: FeeConfigGroupViewModel;
  depth: number;
  isCollapsed: boolean;
  subGroupCount: number;
  configCount: number;
}

interface ConfigRowData {
  kind: 'config';
  key: string;
  config: FeeConfigurationViewModel;
  depth: number;
  label: string;
  isGlobal: boolean;
  rulesCount: number;
  isSelected: boolean;
}

interface EmptyRowData {
  kind: 'empty';
  key: string;
  depth: number;
}

type RowData = GroupRowData | ConfigRowData | EmptyRowData;

interface GroupRowProps {
  group: FeeConfigGroupViewModel;
  depth: number;
  isCollapsed: boolean;
  subGroupCount: number;
  configCount: number;
  isDraft: boolean;
  onToggleCollapse: (args: { groupId: number }) => void;
  onAddSubGroup: (args: { parent: FeeConfigGroupViewModel }) => void;
  onEditGroupLogic: (args: { group: FeeConfigGroupViewModel }) => void;
  onRenameGroup: (args: { group: FeeConfigGroupViewModel }) => void;
  onDeleteGroup: (args: { group: FeeConfigGroupViewModel; target: HTMLElement }) => void;
}

const GroupRow = memo<GroupRowProps>(
  ({ group, depth, isCollapsed, subGroupCount, configCount, isDraft, onToggleCollapse, onAddSubGroup, onEditGroupLogic, onRenameGroup, onDeleteGroup }) => {
    const summaryParts: string[] = [];
    if (subGroupCount > 0) summaryParts.push(`${subGroupCount} sub-group${subGroupCount === 1 ? '' : 's'}`);
    summaryParts.push(`${configCount} config${configCount === 1 ? '' : 's'}`);

    return (
      <div className={`${styles.bandRow} ${styles.groupBand}`}>
        <div className={styles.selectCell}>
          <button
            type="button"
            className={styles.chevronButton}
            onClick={() => onToggleCollapse({ groupId: group.feeConfigGroupId })}
            aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
          >
            <i className={isCollapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-down'} />
          </button>
        </div>
        <div className={styles.mainCell} style={{ paddingLeft: `${depth * INDENT_PER_LEVEL_REM}rem` }}>
          <i className="pi pi-sitemap" style={{ color: '#3b82f6' }} />
          <span className={styles.levelTag}>L{depth + 1}</span>
          <span className={styles.groupName}>{group.name}</span>
          <span className={styles.chip}>{summaryParts.join(' \u00b7 ')}</span>
          {group.qualifyingExpression?.trim() ? (
            <>
              <span className={styles.metaLabel}>IF</span>
              <span className={styles.ifChip} title={group.qualifyingExpression}>
                {group.qualifyingExpression}
              </span>
            </>
          ) : (
            <span className={styles.alwaysEvaluated}>
              <i className="pi pi-bolt" /> Always evaluated
            </span>
          )}
        </div>
        <div className={styles.countCell} />
        <div className={styles.actionsCell}>
          <Button
            icon="pi pi-plus"
            className="p-button-text p-button-sm"
            onClick={() => onAddSubGroup({ parent: group })}
            disabled={!isDraft}
            tooltip="Add nested sub-group"
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-sliders-h"
            className="p-button-text p-button-sm"
            onClick={() => onEditGroupLogic({ group })}
            disabled={!isDraft}
            tooltip="Edit group logic"
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-pencil"
            className="p-button-text p-button-sm"
            onClick={() => onRenameGroup({ group })}
            disabled={!isDraft}
            tooltip="Rename group"
            tooltipOptions={{ position: 'top' }}
          />
          <Button
            icon="pi pi-trash"
            className="p-button-text p-button-sm p-button-danger"
            onClick={(event) => onDeleteGroup({ group, target: event.currentTarget })}
            disabled={!isDraft}
            style={!isDraft ? { opacity: 0.3 } : undefined}
            tooltip="Delete group"
            tooltipOptions={{ position: 'top' }}
          />
        </div>
      </div>
    );
  }
);
GroupRow.displayName = 'GroupRow';

interface ConfigRowProps {
  config: FeeConfigurationViewModel;
  depth: number;
  label: string;
  isGlobal: boolean;
  rulesCount: number;
  isSelected: boolean;
  isDraft: boolean;
  onToggleConfigSelected: (args: { configId: number }) => void;
  onEditConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onCopyConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onMoveConfig: (args: { config: FeeConfigurationViewModel }) => void;
  onDeleteConfig: (args: { config: FeeConfigurationViewModel; target: HTMLElement }) => void;
}

const ConfigRow = memo<ConfigRowProps>(
  ({ config, depth, label, isGlobal, rulesCount, isSelected, isDraft, onToggleConfigSelected, onEditConfig, onCopyConfig, onMoveConfig, onDeleteConfig }) => (
    <div className={`${styles.row} ${styles.configRow}`}>
      <div className={styles.selectCell}>
        <Checkbox checked={isSelected} onChange={() => onToggleConfigSelected({ configId: config.feeConfigurationId })} disabled={!isDraft} />
      </div>
      <div className={styles.mainCell} style={{ paddingLeft: `${depth * INDENT_PER_LEVEL_REM}rem` }}>
        {isGlobal && <span className={styles.globalTag}>GLOBAL</span>}
        <span style={{ wordBreak: 'break-word' }}>{label}</span>
      </div>
      <div className={styles.countCell}>{rulesCount}</div>
      <div className={styles.actionsCell}>
        <Button
          icon={isDraft ? 'pi pi-pencil' : 'pi pi-eye'}
          className="p-button-text p-button-sm"
          onClick={() => onEditConfig({ config })}
          tooltip={isDraft ? 'Edit configuration' : 'View configuration'}
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-clone"
          className="p-button-text p-button-sm"
          onClick={() => onCopyConfig({ config })}
          disabled={!isDraft}
          tooltip="Copy configuration"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-arrow-right-arrow-left"
          className="p-button-text p-button-sm"
          onClick={() => onMoveConfig({ config })}
          disabled={!isDraft}
          tooltip="Move to group"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={(event) => onDeleteConfig({ config, target: event.currentTarget })}
          disabled={!isDraft}
          style={!isDraft ? { opacity: 0.3 } : undefined}
          tooltip="Delete configuration"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    </div>
  )
);
ConfigRow.displayName = 'ConfigRow';

export const FeeConfigGroupTree: FC<FeeConfigGroupTreeProps> = ({
  groups,
  configurations,
  hudLineMap,
  isDraft,
  search,
  filterActive,
  selectedConfigIds,
  collapsedGroupIds,
  ungroupedCollapsed,
  onToggleConfigSelected,
  onToggleCollapse,
  onToggleUngrouped,
  onEditConfig,
  onCopyConfig,
  onMoveConfig,
  onDeleteConfig,
  onAddSubGroup,
  onEditGroupLogic,
  onRenameGroup,
  onDeleteGroup,
}) => {
  const searchLower = search.trim().toLowerCase();
  const filtering = filterActive ?? !!searchLower;

  const childGroupsByParent = useMemo(() => {
    const map = new Map<number | null, FeeConfigGroupViewModel[]>();
    for (const group of groups) {
      // The API omits null fields (WhenWritingNull), so a root group's parentFeeConfigGroupId
      // arrives as undefined; normalize to null so it keys under the root bucket.
      const key = group.parentFeeConfigGroupId ?? null;
      const siblings = map.get(key) ?? [];
      siblings.push(group);
      map.set(key, siblings);
    }
    for (const siblings of map.values()) {
      siblings.sort((first, second) => first.sortOrder - second.sortOrder || first.name.localeCompare(second.name));
    }
    return map;
  }, [groups]);

  const configsByGroup = useMemo(() => {
    const map = new Map<number | null, FeeConfigurationViewModel[]>();
    for (const config of configurations) {
      const key = config.feeConfigGroupId ?? null;
      const siblings = map.get(key) ?? [];
      siblings.push(config);
      map.set(key, siblings);
    }
    return map;
  }, [configurations]);

  // A group is visible when there is no search, when its own name matches, or when any config in
  // its subtree matches the search.
  const groupHasMatch = useMemo(() => {
    const cache = new Map<number, boolean>();
    const compute = (group: FeeConfigGroupViewModel): boolean => {
      if (cache.has(group.feeConfigGroupId)) return cache.get(group.feeConfigGroupId)!;
      const nameMatches = !!searchLower && group.name.toLowerCase().includes(searchLower);
      const ownConfigMatch = (configsByGroup.get(group.feeConfigGroupId) ?? []).some((config) =>
        configMatchesSearch(config, hudLineMap, searchLower)
      );
      const childMatch = (childGroupsByParent.get(group.feeConfigGroupId) ?? []).some(compute);
      const result = !filtering || nameMatches || ownConfigMatch || childMatch;
      cache.set(group.feeConfigGroupId, result);
      return result;
    };
    for (const group of groups) compute(group);
    return cache;
  }, [groups, childGroupsByParent, configsByGroup, hudLineMap, searchLower, filtering]);

  const configLabel = (config: FeeConfigurationViewModel): string => {
    const hudLineValue = config.feeHudLineId != null ? hudLineMap.get(config.feeHudLineId)?.hudLine : undefined;
    const description = config.description ?? '-';
    return hudLineValue ? `${hudLineValue} - ${description}` : description;
  };

  const toConfigRow = (config: FeeConfigurationViewModel, depth: number): ConfigRowData => ({
    kind: 'config',
    key: `config-${config.feeConfigurationId}`,
    config,
    depth,
    label: configLabel(config),
    isGlobal: config.isGlobal || config.feeHudLineId == null,
    rulesCount: config.rules?.length ?? 0,
    isSelected: selectedConfigIds.has(config.feeConfigurationId),
  });

  const rootGroups = childGroupsByParent.get(null) ?? [];
  const ungroupedConfigs = configsByGroup.get(null) ?? [];
  const visibleUngrouped = ungroupedConfigs.filter((config) => configMatchesSearch(config, hudLineMap, searchLower));
  const showUngroupedBand = !filtering || visibleUngrouped.length > 0;

  // Flatten the visible tree into an ordered row list. Only expanded (or, when filtering, matching)
  // subtrees are walked, so collapsed branches never mount and one toggle touches a bounded set.
  const groupRows = useMemo(() => {
    const rows: RowData[] = [];
    const walk = (group: FeeConfigGroupViewModel, depth: number) => {
      if (filtering && !groupHasMatch.get(group.feeConfigGroupId)) return;

      const childGroups = childGroupsByParent.get(group.feeConfigGroupId) ?? [];
      const groupConfigs = configsByGroup.get(group.feeConfigGroupId) ?? [];
      const visibleConfigs = groupConfigs.filter((config) => configMatchesSearch(config, hudLineMap, searchLower));
      // A search reveals matches inside otherwise-collapsed subtrees, so ignore collapse while filtering.
      const isCollapsed = !filtering && collapsedGroupIds.has(group.feeConfigGroupId);

      rows.push({
        kind: 'group',
        key: `group-${group.feeConfigGroupId}`,
        group,
        depth,
        isCollapsed,
        subGroupCount: childGroups.length,
        configCount: groupConfigs.length,
      });

      if (!isCollapsed) {
        for (const child of childGroups) walk(child, depth + 1);
        for (const config of visibleConfigs) rows.push(toConfigRow(config, depth + 1));
        if (childGroups.length === 0 && visibleConfigs.length === 0) {
          rows.push({ kind: 'empty', key: `empty-${group.feeConfigGroupId}`, depth: depth + 1 });
        }
      }
    };
    for (const group of rootGroups) walk(group, 0);
    return rows;
    // toConfigRow / configLabel close over hudLineMap and selectedConfigIds, both listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootGroups, childGroupsByParent, configsByGroup, groupHasMatch, collapsedGroupIds, selectedConfigIds, hudLineMap, searchLower, filtering]);

  const hasAnyContent = showUngroupedBand || groupRows.length > 0;

  const renderRow = (row: RowData) => {
    if (row.kind === 'group') {
      return (
        <GroupRow
          key={row.key}
          group={row.group}
          depth={row.depth}
          isCollapsed={row.isCollapsed}
          subGroupCount={row.subGroupCount}
          configCount={row.configCount}
          isDraft={isDraft}
          onToggleCollapse={onToggleCollapse}
          onAddSubGroup={onAddSubGroup}
          onEditGroupLogic={onEditGroupLogic}
          onRenameGroup={onRenameGroup}
          onDeleteGroup={onDeleteGroup}
        />
      );
    }
    if (row.kind === 'config') {
      return (
        <ConfigRow
          key={row.key}
          config={row.config}
          depth={row.depth}
          label={row.label}
          isGlobal={row.isGlobal}
          rulesCount={row.rulesCount}
          isSelected={row.isSelected}
          isDraft={isDraft}
          onToggleConfigSelected={onToggleConfigSelected}
          onEditConfig={onEditConfig}
          onCopyConfig={onCopyConfig}
          onMoveConfig={onMoveConfig}
          onDeleteConfig={onDeleteConfig}
        />
      );
    }
    return (
      <div key={row.key} className={styles.emptyGroup} style={{ paddingLeft: `${row.depth * INDENT_PER_LEVEL_REM + 0.75}rem` }}>
        No configurations in this group yet.
      </div>
    );
  };

  return (
    <div className={styles.tree}>
      <div className={styles.headerRow}>
        <div className={styles.selectCell} />
        <div className={styles.mainCell}>HUD Line / Description</div>
        <div className={styles.countCell}>Rules Count</div>
        <div className={styles.actionsCell} style={{ justifyContent: 'flex-end' }}>Actions</div>
      </div>

      {showUngroupedBand && (
        <div>
          <div className={`${styles.bandRow} ${styles.ungroupedBand}`}>
            <div className={styles.selectCell}>
              <button
                type="button"
                className={styles.chevronButton}
                onClick={onToggleUngrouped}
                aria-label={ungroupedCollapsed ? 'Expand ungrouped' : 'Collapse ungrouped'}
              >
                <i className={ungroupedCollapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-down'} />
              </button>
            </div>
            <div className={styles.mainCell}>
              <i className="pi pi-folder" style={{ color: '#6b7280' }} />
              <span className={styles.bandTitle}>Ungrouped configurations</span>
              <span className={styles.chip}>{ungroupedConfigs.length} config{ungroupedConfigs.length === 1 ? '' : 's'}</span>
            </div>
            <div className={styles.countCell} />
            <div className={styles.actionsCell}>
              <span className={styles.alwaysEvaluated}>
                <i className="pi pi-bolt" /> Always evaluated
              </span>
            </div>
          </div>
          {!ungroupedCollapsed &&
            (visibleUngrouped.length > 0 ? (
              visibleUngrouped.map((config) => renderRow(toConfigRow(config, 0)))
            ) : (
              <div className={styles.emptyGroup} style={{ paddingLeft: '2.75rem' }}>
                No ungrouped configurations.
              </div>
            ))}
        </div>
      )}

      {groupRows.map(renderRow)}

      {!hasAnyContent && <div className={styles.emptyState}>No fee configurations or groups found.</div>}
    </div>
  );
};
